import { useState, useContext } from 'react';
import { AppContext } from 'src/AppContext';

import InfiniteScroll from "react-infinite-scroll-component";
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Skeleton from '@mui/material/Skeleton';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid2';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import Stack, { StackProps } from '@mui/material/Stack';
import CircularProgress from '@mui/material/CircularProgress';
import StarIcon from '@mui/icons-material/Star';
import PlaceIcon from '@mui/icons-material/Place';
import Iconify from 'src/components/iconify';
import Image from 'src/components/image';
import TextMaxLine from 'src/components/text-max-line';
import { gray } from 'src/templates/shared-theme/themePrimitives';

export default function ProfileContent({ loading, profiles, onLoadMore, hasMore, onRefresh }) {
  const { user } = useContext(AppContext);

  return (
    <InfiniteScroll
      dataLength={profiles?.profiles.length || 0}
      next={onLoadMore}
      hasMore={hasMore}
      loader={
        <Box sx={{ textAlign: "center", padding: 2 }}>
          <CircularProgress size={20} />
        </Box>
      }
      endMessage={
        <Typography align="center" color="text.secondary" sx={{ padding: 2 }}>
          {user ? "Yay! You have seen it all 🎉" : "Sign in to see more!"}
        </Typography>
      }
      refreshFunction={onRefresh}
      pullDownToRefresh
      pullDownToRefreshThreshold={50}
      pullDownToRefreshContent={
        <Typography align="center" color="primary" sx={{ padding: 2 }}>
          Pull down to refresh...
        </Typography>
      }
      releaseToRefreshContent={
        <Typography align="center" color="primary" sx={{ padding: 2 }}>
          Release to refresh...
        </Typography>
      }
    >
      {(loading ? [...Array(8)] : profiles.profiles).map((profile, index) =>
        profile ? (
          <ViewListItem key={profile.id} profile={profile} />
        ) : (
          <ViewListItemSkeleton key={index} />
        )
      )}
    </InfiniteScroll>
  )
}

function ViewListItem({ profile, sx, ...other }) {
  return (
    <Stack
      direction="row"
      sx={{
        mb: 2,
        p: 1,
        position: 'relative',
        boxShadow: 'hsla(220, 30%, 5%, 0.05) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.05) 0px 15px 35px -5px',
        ...sx,
      }}
      {...other}
    >
      <Box sx={{ position: 'relative' }}>
        <Image
          src={profile.coverImageURL}
          sx={{
            mr: 2,
            width: 100,
            height: 100,
            flexShrink: 0,
            borderRadius: 1.5,
            bgcolor: 'background.neutral',
          }}
        />
      </Box>

      <Stack
        spacing={0.5}
        sx={{
          width: '100%',
        }}
      >
        <Stack
          direction="row"
          justifyContent='space-between'
          alignItems='center'
        >
          <TextMaxLine variant="h5" line={1}>
            {profile.userName}
          </TextMaxLine>
          <Typography variant="body" fontSize="18px" sx={{ color: "#ED4848" }}>
            S${profile.price}
          </Typography>
        </Stack>

        <Stack
          direction="row"
          sx={{
            alignItems: 'center',
          }}
        >
          <StarIcon fontSize="small" sx={{ color: "#ED4848" }} />
          <Typography
            fontSize="16px"
            sx={{ mt: 0.1, color: "#ED4848" }}
          >
            {profile.averageRating > 0 ? profile.averageRating : " "}
          </Typography>
          <Typography fontSize="16px" sx={{ mt: 0.1, mx: 1, color: 'text.disabled' }} >
            /
          </Typography>
          {
            profile.yearsOfExperience > 0 &&
            <Typography fontSize="14px" sx={{ mt: 0.1, color: gray[800] }} >
              {profile.yearsOfExperience} {profile.yearsOfExperience > 1 ? "years" : "year"} of experience
            </Typography>
          }
        </Stack>

        <Stack >
          <Stack
            spacing={1}
            direction="row"
            flexWrap="wrap"
            useFlexGap
            sx={{
              alignItems: 'center',
            }}
          >
            {profile.expertise.map((expertise, index) => {
              return (
                <Chip key={index} label={expertise} color={'primary'} size="small" sx={{ fontSize: "12px" }} />
              )
            })}
          </Stack>
        </Stack>
        {profile.regions &&
          <Stack direction="row" alignItems="center">
            <PlaceIcon fontSize="small" sx={{ color: gray[400] }} />
            <Typography fontSize="14px" sx={{ mt: 0.1, color: gray[600] }}>
              {profile.regions?.join(" | ")}
            </Typography>
          </Stack>
        }
      </Stack>
    </Stack>
  )
}

function ViewListItemSkeleton() {
  return (
    <Stack spacing={2} direction="row"
      sx={{
        mb: 2,
        p: 1,
      }}
    >
      <Skeleton
        variant="rounded"
        width={160}
        height={160}
        sx={{ flexShrink: 0, borderRadius: 2 }}
      />

      <Stack flexGrow={1.5} spacing={1}>
        <Skeleton variant="rounded" height={16} width="90%" />

        <Skeleton variant="rounded" height={16} width="80%" />

        <Skeleton variant="rounded" height={16} width="70%" />

        <Skeleton variant="rounded" height={16} width="60%" />

        <Skeleton variant="rounded" height={16} width="50%" />
      </Stack>
    </Stack>
  );
}
